import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { StorageResourceProperties, StorageResourceRef } from '../../../../../types/azureStorage';
import { cancelStorageRequest, getStorageResourceProperties, newStorageRequestId, toStorageOperationError } from '../../services/azureStorageApi';
import { storageBlobPropertiesQueryKey, storageContainerPropertiesQueryKey } from '../../services/storageListingKeys';

type PropertiesRef = Extract<StorageResourceRef, { kind: 'blob' | 'container' }>;
const READ_TIMEOUT_MS = 30_000;

export function useStorageProperties(args: { connectionId: string; resource: PropertiesRef; enabled: boolean }) {
  const { connectionId, resource, enabled } = args;
  const queryClient = useQueryClient();
  const activeCancel = useRef<(() => Promise<void>) | null>(null);
  const key = useMemo(
    () => resource.kind === 'blob'
      ? storageBlobPropertiesQueryKey(connectionId, resource)
      : storageContainerPropertiesQueryKey(connectionId, resource),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resource identity is represented by its fields
    [connectionId, resource.kind, resource.container, resource.kind === 'blob' ? resource.name : '', resource.kind === 'blob' ? resource.snapshot : '', resource.kind === 'blob' ? resource.versionId : ''],
  );
  const query = useQuery({
    queryKey: key,
    enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<StorageResourceProperties> => {
      const requestId = newStorageRequestId();
      let didCancel = false;
      const cancelOnce = async () => {
        if (didCancel) return;
        didCancel = true;
        await cancelStorageRequest(requestId);
      };
      activeCancel.current = cancelOnce;
      const onAbort = () => void cancelOnce();
      signal.addEventListener('abort', onAbort);
      try {
        return await getStorageResourceProperties({ connectionId, resource, requestId, timeoutMs: READ_TIMEOUT_MS });
      } finally {
        signal.removeEventListener('abort', onAbort);
        if (activeCancel.current === cancelOnce) activeCancel.current = null;
      }
    },
  });
  // The panel and the tier dialog share this key (Fable F10): the last observer to leave cancels the read and drops the
  // cached value; while another still observes it, its value and its in-flight read are left alone (no reload flicker).
  useEffect(() => () => {
    if ((queryClient.getQueryCache().find({ queryKey: key, exact: true })?.getObserversCount() ?? 0) > 0) return;
    void activeCancel.current?.();
    queryClient.removeQueries({ queryKey: key, exact: true });
  }, [key, queryClient]);
  return {
    data: query.data,
    error: query.error ? toStorageOperationError(query.error) : undefined,
    isLoading: query.isPending,
    isFetching: query.isFetching,
    reload: () => void queryClient.resetQueries({ queryKey: key, exact: true }),
    /** Re-reads in the background, keeping the current value visible (the panel's Reload). */
    refetch: () => void queryClient.invalidateQueries({ queryKey: key, exact: true }),
    /**
     * Re-reads now and answers whether the read succeeded (UI/UX fix round A, A2): the panel discards a draft only AFTER a
     * successful reload, so a failed reload keeps the user's edits.
     */
    refetchNow: async (): Promise<boolean> => {
      await queryClient.refetchQueries({ queryKey: key, exact: true });
      return queryClient.getQueryState(key)?.status !== 'error';
    },
  };
}
